import React, { useEffect, useState, useCallback } from "react";
import { toast } from "sonner";
import { api, money, apiErr } from "@/lib/api";
import { Table, Button, Badge, Input, Select, Field, SortControl, sortRows } from "@/lib/ui";

const TABS = [
  { k: "visitors", t: "Visitor Directory", i: "fa-address-book" },
  { k: "payments", t: "Payments", i: "fa-receipt" },
  { k: "testimonials", t: "Testimonials", i: "fa-star" },
  { k: "buyers", t: "Buyers", i: "fa-user" },
  { k: "disclaimers", t: "Disclaimers", i: "fa-scroll" },
  { k: "restricted", t: "Restricted Attempts", i: "fa-triangle-exclamation" },
];

export default function AdminExtras() {
  const [tab, setTab] = useState("visitors");
  const [payments, setPayments] = useState([]);
  const [testimonials, setTestimonials] = useState([]);
  const [buyers, setBuyers] = useState([]);
  const [disc, setDisc] = useState([]);
  const [attempts, setAttempts] = useState([]);
  const [visitors, setVisitors] = useState([]);
  const [vq, setVq] = useState({ vtype: "", search: "" });
  const [dform, setDform] = useState({ text: "", location: "Product Detail", display_type: "Inline Message", frequency: "Every Visit" });
  const [discId, setDiscId] = useState(null);
  const [sort, setSort] = useState({ field: "last_seen", dir: "desc" });
  const FIELDS = {
    visitors: [{ key: "last_seen", label: "Last Seen", type: "date" }, { key: "first_seen", label: "First Seen", type: "date" }, { key: "visits", label: "Visits", type: "num" }, { key: "phone", label: "Phone", type: "str" }],
    payments: [{ key: "created_at", label: "Date", type: "date" }, { key: "amount", label: "Amount", type: "num" }, { key: "company_name", label: "Company", type: "str" }, { key: "status", label: "Status", type: "str" }],
    testimonials: [{ key: "company_name", label: "Company", type: "str" }, { key: "status", label: "Status", type: "str" }],
    buyers: [{ key: "created_at", label: "First Visit", type: "date" }, { key: "phone", label: "Phone", type: "str" }],
    disclaimers: [{ key: "location", label: "Location", type: "str" }, { key: "display_type", label: "Display", type: "str" }, { key: "status", label: "Status", type: "str" }],
    restricted: [{ key: "created_at", label: "Date", type: "date" }, { key: "matched", label: "Matched", type: "str" }, { key: "category", label: "Category", type: "str" }],
  };
  useEffect(() => {
    const f = (FIELDS[tab] || []).find((x) => x.type === "str") || FIELDS[tab]?.[0];
    if (f) setSort({ field: f.key, dir: "asc" });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tab]);

  const loadVisitors = useCallback(() => {
    const params = {};
    Object.entries(vq).forEach(([k, v]) => v && (params[k] = v));
    api.get("/admin/visitors", { params }).then(({ data }) => setVisitors(data));
  }, [vq]);

  const load = () => {
    api.get("/admin/payments").then(({ data }) => setPayments(data));
    api.get("/admin/testimonials").then(({ data }) => setTestimonials(data));
    api.get("/admin/buyers").then(({ data }) => setBuyers(data));
    api.get("/admin/disclaimers").then(({ data }) => setDisc(data));
    api.get("/admin/restricted-attempts").then(({ data }) => setAttempts(data));
  };
  useEffect(() => { load(); }, []);
  useEffect(() => { const t = setTimeout(loadVisitors, 200); return () => clearTimeout(t); }, [loadVisitors]);

  const payAction = async (id, status) => { try { await api.post(`/admin/payments/${id}/status?status=${status}`); toast.success(`Payment ${status}`); load(); } catch (e) { toast.error(apiErr(e)); } };
  const testAction = async (id, status) => { await api.post(`/admin/testimonials/${id}/status?status=${status}`); toast.success(`Testimonial ${status}`); load(); };
  const addDisc = async (e) => {
    e.preventDefault();
    if (!dform.text.trim()) return;
    try {
      if (discId) { await api.put(`/admin/disclaimers/${discId}`, { ...dform, status: "active" }); toast.success("Disclaimer updated"); }
      else { await api.post("/admin/disclaimers", { ...dform, status: "active" }); toast.success("Disclaimer added"); }
      setDform({ text: "", location: "Product Detail", display_type: "Inline Message", frequency: "Every Visit" }); setDiscId(null); load();
    } catch (er) { toast.error(apiErr(er)); }
  };
  const editDisc = (d) => { setDiscId(d.id); setDform({ text: d.text, location: d.location, display_type: d.display_type, frequency: d.frequency }); };
  const delDisc = async (id) => { await api.delete(`/admin/disclaimers/${id}`); if (discId === id) { setDiscId(null); setDform({ text: "", location: "Product Detail", display_type: "Inline Message", frequency: "Every Visit" }); } load(); };

  return (
    <div>
      <h1 className="font-head text-2xl font-extrabold text-slate-900">Operations</h1>
      <div className="mt-4 flex flex-wrap gap-2">
        {TABS.map((t) => (
          <button key={t.k} onClick={() => setTab(t.k)} data-testid={`extras-${t.k}`}
            className={`rounded-lg px-4 py-2 text-sm font-semibold ${tab === t.k ? "bg-slate-900 text-white" : "bg-white text-slate-600 border border-slate-200"}`}>
            <i className={`fa-solid ${t.i} mr-1`} /> {t.t}
          </button>
        ))}
      </div>

      {FIELDS[tab] && <div className="mt-4"><SortControl fields={FIELDS[tab]} value={sort} onChange={setSort} testid="extras-sort" /></div>}

      <div className="mt-5">
        {tab === "visitors" && (
          <>
            <div className="mb-4 flex flex-wrap gap-2">
              <Input className="max-w-xs" placeholder="Search phone / company…" value={vq.search} onChange={(e) => setVq((p) => ({ ...p, search: e.target.value }))} data-testid="visitors-search" />
              <Select className="w-auto" value={vq.vtype} onChange={(e) => setVq((p) => ({ ...p, vtype: e.target.value }))} data-testid="visitors-type">
                <option value="">All Visitors</option><option value="buyer">Buyers</option><option value="seller">Sellers</option>
              </Select>
              <Button variant="outline" onClick={() => setVq({ vtype: "", search: "" })}><i className="fa-solid fa-rotate-left" /> Reset</Button>
            </div>
            <Table head={["Phone", "Visitor Type", "Details", "Visits", "First Seen", "Last Seen"]}>
              {sortRows(visitors, FIELDS.visitors, sort).map((v) => (
                <tr key={v.id} data-testid={`visitor-row-${v.id}`}>
                  <td className="px-4 py-3 font-semibold text-slate-800">+91 {v.phone}</td>
                  <td className="px-4 py-3"><Badge tone={v.type === "seller" ? "blue" : "emeraldSoft"}><i className={`fa-solid ${v.type === "seller" ? "fa-store" : "fa-cart-shopping"}`} /> {v.type}</Badge></td>
                  <td className="px-4 py-3 text-slate-600">{v.company_name || (v.seller_type ? `${v.seller_type}` : "—")}{v.seller_type && v.company_name ? ` · ${v.seller_type}` : ""}</td>
                  <td className="px-4 py-3">{v.visits}</td>
                  <td className="px-4 py-3 text-slate-500">{new Date(v.first_seen).toLocaleString()}</td>
                  <td className="px-4 py-3 text-slate-500">{new Date(v.last_seen).toLocaleString()}</td>
                </tr>
              ))}
              {visitors.length === 0 && <tr><td colSpan={6} className="px-4 py-8 text-center text-slate-400">No visitors yet</td></tr>}
            </Table>
          </>
        )}

        {tab === "payments" && (
          <Table head={["Company", "Type", "Plan", "Amount", "Status", "Date", "Actions"]}>
            {sortRows(payments, FIELDS.payments, sort).map((p) => (
              <tr key={p.id} data-testid={`pay-row-${p.id}`}>
                <td className="px-4 py-3 font-semibold">{p.company_name}</td><td className="px-4 py-3 capitalize">{p.type}</td>
                <td className="px-4 py-3">{p.plan_name}</td><td className="px-4 py-3 font-semibold">{money(p.amount)}</td>
                <td className="px-4 py-3"><Badge tone={p.status === "approved" ? "green" : p.status === "rejected" ? "red" : "amber"}>{p.status}</Badge></td>
                <td className="px-4 py-3 text-slate-500">{new Date(p.created_at).toLocaleDateString()}</td>
                <td className="px-4 py-3">
                  {p.status !== "approved" && <Button variant="green" onClick={() => payAction(p.id, "approved")} data-testid={`pay-approve-${p.id}`}>Approve</Button>}
                  {p.status !== "rejected" && <Button variant="ghost" onClick={() => payAction(p.id, "rejected")}>Reject</Button>}
                </td>
              </tr>
            ))}
            {payments.length === 0 && <tr><td colSpan={7} className="px-4 py-8 text-center text-slate-400">No payments</td></tr>}
          </Table>
        )}

        {tab === "testimonials" && (
          <Table head={["Company", "Type", "Text Review", "Video", "Status", "Actions"]}>
            {sortRows(testimonials, FIELDS.testimonials, sort).map((t) => (
              <tr key={t.id}>
                <td className="px-4 py-3 font-semibold">{t.company_name}</td><td className="px-4 py-3 capitalize">{t.seller_type}</td>
                <td className="px-4 py-3 max-w-xs text-slate-600">{t.text_review}</td>
                <td className="px-4 py-3">{t.video_url ? <a href={t.video_url} target="_blank" rel="noreferrer" className="text-blue-600">View</a> : "—"}</td>
                <td className="px-4 py-3"><Badge tone={t.status === "published" ? "green" : "amber"}>{t.status}</Badge></td>
                <td className="px-4 py-3">
                  <Button variant="green" onClick={() => testAction(t.id, "published")}>Publish</Button>
                  <Button variant="ghost" onClick={() => testAction(t.id, "rejected")}>Reject</Button>
                </td>
              </tr>
            ))}
            {testimonials.length === 0 && <tr><td colSpan={6} className="px-4 py-8 text-center text-slate-400">No testimonials</td></tr>}
          </Table>
        )}

        {tab === "buyers" && (
          <>
            <p className="mb-2 text-sm text-slate-500">Total unique buyers visited so far: <b className="text-slate-900" data-testid="buyers-total">{buyers.length}</b></p>
            <Table head={["S. No.", "Phone", "First Visit"]}>
              {sortRows(buyers, FIELDS.buyers, sort).map((b, i) => (<tr key={b.id} data-testid={`buyer-row-${b.id}`}><td className="px-4 py-3 font-bold text-slate-700">{i + 1}</td><td className="px-4 py-3 font-semibold">+91 {b.phone}</td><td className="px-4 py-3 text-slate-500">{new Date(b.created_at).toLocaleString()}</td></tr>))}
              {buyers.length === 0 && <tr><td colSpan={3} className="px-4 py-8 text-center text-slate-400">No buyers</td></tr>}
            </Table>
          </>
        )}

        {tab === "disclaimers" && (
          <>
            <form onSubmit={addDisc} className="mb-4 grid gap-3 rounded-xl border border-slate-200 bg-white p-4 sm:grid-cols-4">
              <div className="sm:col-span-4"><Field label="Disclaimer Text"><Input value={dform.text} onChange={(e) => setDform((f) => ({ ...f, text: e.target.value }))} data-testid="disc-text" /></Field></div>
              <Field label="Location"><Select value={dform.location} onChange={(e) => setDform((f) => ({ ...f, location: e.target.value }))}><option>Product Detail</option><option>Add Product</option><option>WhatsApp Seller Page</option><option>Buyer Marketplace</option><option>Footer</option></Select></Field>
              <Field label="Display Type"><Select value={dform.display_type} onChange={(e) => setDform((f) => ({ ...f, display_type: e.target.value }))}><option>Inline Message</option><option>Banner</option><option>Popup</option><option>Checkbox Acknowledgement</option><option>Footer Disclaimer</option></Select></Field>
              <Field label="Frequency"><Select value={dform.frequency} onChange={(e) => setDform((f) => ({ ...f, frequency: e.target.value }))}><option>Every Visit</option><option>Once / Session</option><option>Once / User</option><option>Before Continuing</option><option>Until Acknowledged</option></Select></Field>
              <div className="flex items-end gap-2">
                <Button type="submit" data-testid="disc-add"><i className={`fa-solid ${discId ? "fa-check" : "fa-plus"}`} /> {discId ? "Update" : "Add"}</Button>
                {discId && <Button type="button" variant="outline" onClick={() => { setDiscId(null); setDform({ text: "", location: "Product Detail", display_type: "Inline Message", frequency: "Every Visit" }); }}>Cancel</Button>}
              </div>
            </form>
            <Table head={["Text", "Location", "Display", "Frequency", "Status", "Actions"]}>
              {sortRows(disc, FIELDS.disclaimers, sort).map((d) => (
                <tr key={d.id}>
                  <td className="px-4 py-3 max-w-xs text-slate-600">{d.text}</td><td className="px-4 py-3">{d.location}</td>
                  <td className="px-4 py-3">{d.display_type}</td><td className="px-4 py-3">{d.frequency}</td>
                  <td className="px-4 py-3"><Badge tone={d.status === "active" ? "green" : "slate"}>{d.status}</Badge></td>
                  <td className="px-4 py-3">
                    <div className="flex gap-1">
                      <Button variant="outline" onClick={() => editDisc(d)} data-testid={`disc-edit-${d.id}`}><i className="fa-solid fa-pen" /></Button>
                      <Button variant="ghost" onClick={() => delDisc(d.id)}><i className="fa-solid fa-trash text-red-500" /></Button>
                    </div>
                  </td>
                </tr>
              ))}
            </Table>
          </>
        )}

        {tab === "restricted" && (
          <Table head={["Matched", "Category", "Product", "Seller", "Action", "Date"]}>
            {sortRows(attempts, FIELDS.restricted, sort).map((a) => (
              <tr key={a.id}>
                <td className="px-4 py-3 font-semibold text-red-600">{a.matched}</td><td className="px-4 py-3">{a.category}</td>
                <td className="px-4 py-3">{a.product}</td><td className="px-4 py-3">{a.company_name || "—"}</td>
                <td className="px-4 py-3"><Badge tone="red">{a.action}</Badge></td>
                <td className="px-4 py-3 text-slate-500">{new Date(a.created_at).toLocaleString()}</td>
              </tr>
            ))}
            {attempts.length === 0 && <tr><td colSpan={6} className="px-4 py-8 text-center text-slate-400">No restricted attempts logged</td></tr>}
          </Table>
        )}
      </div>
    </div>
  );
}
